<template>
  <div class="rent-success-wrapper">
    <!-- Main Success Card -->
    <!-- Modern Status Box at the Top -->
    <div
      v-if="lastTransaction"
      class="status-card shadow-sm"
    >
      <div class="status-header">
        <div class="status-indicator">
          <div class="status-dot-pulse"></div>
          <span class="status-title">{{ $t('common.rental') }}</span>
        </div>
      </div>

      <div class="info-row">
        <div class="info-item">
          <font-awesome-icon
            icon="clock"
            class="info-icon"
          />
          <div class="info-content">
            <span class="info-label">{{ $t('home.return.time') }}</span>
            <span class="info-value">{{ convertMinutesToHours(elapsedTime) }}</span>
          </div>
        </div>

        <div class="info-divider"></div>

        <div class="info-item">
          <font-awesome-icon
            icon="dollar-sign"
            class="info-icon"
          />
          <div class="info-content">
            <span class="info-label">{{ $t('home.return.price') }}</span>
            <span class="info-value">{{ formatToBrazilianReal(lastTransaction.price) }}</span>
          </div>
        </div>
      </div>

      <div
        v-if="lastTransaction.on_free_time"
        class="free-time-bar"
      >
        <font-awesome-icon
          icon="bolt"
          class="free-icon"
        />
        <span class="free-text">
          {{ $t('home.return.free_time') }}: <strong>{{ convertMinutesToHours(lastTransaction.next_payment) }}</strong>
        </span>
      </div>
    </div>
    <div class="select-card success-card d-flex flex-column">
      <div class="success-header text-center">
        <div class="icon-circle">
          <font-awesome-icon icon="circle-check" />
        </div>
        <h4 class="success-title mt-1">
          {{ $t('home.return.success') }}
        </h4>
        <p class="success-instructions text-muted">
          {{ $t('home.return.instructions') }}
        </p>
      </div>

      <div class="gif-wrapper non-clickable my-4">
        <img
          src="@/assets/images/cables/cable_opening_and_reverse.gif"
          alt="cabos na bateria"
          class="instruction-gif"
        >
      </div>

      <div class="footer-actions mt-auto">
        <div
          class="action-btn mb-3"
          @click="handleShowMap"
        >
          <font-awesome-icon
            icon="map-location-dot"
            class="btn-icon"
          />
          <div class="btn-text">
            <span>{{ $t('home.return.map_1') }} <span class="link-underline">{{ $t('home.return.link_map') }}</span>
              {{ $t('home.return.map_2') }}</span>
          </div>
        </div>

        <div
          class="action-btn"
          @click="handleShowPriceTable"
        >
          <font-awesome-icon
            icon="calculator"
            class="btn-icon"
          />
          <div class="btn-text">
            <span>{{ $t('home.return.check_out') }}
              <span class="link-underline">{{ $t('home.return.price_table') }}</span></span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- FAQ Section -->
  <div class="faq-section mt-4 mb-5">
    <h5 class="faq-title-main text-center mb-4">
      {{ $t('home.return.faqs.title') }}
    </h5>

    <div class="faq-container">
      <div
        v-for="(item, index) in faqItems"
        :key="index"
        class="faq-item shadow-sm"
        :class="{ active: activeIndex === index }"
      >
        <BaseButton
          bare
          variant=""
          class="faq-header-btn"
          :class="{ active: activeIndex === index }"
          @click="toggleAccordion(index)"
        >
          <span class="faq-question">{{ item.title }}</span>
          <font-awesome-icon
            :icon="activeIndex === index ? 'angle-up' : 'angle-down'"
            class="faq-icon"
          />
        </BaseButton>

        <transition
          name="accordion"
          @enter="startTransition"
          @after-enter="endTransition"
          @before-leave="startTransition"
          @after-leave="endTransition"
        >
          <div
            v-show="activeIndex === index"
            class="faq-collapse"
          >
            <div class="faq-content">
              <template v-if="item.type === 'html'">
                <div>
                  {{ $t(`home.return.faqs.${item.key}.html_1`) }}
                  <a
                    class="faq-link"
                    @click="handleShowMap"
                  >
                    {{ $t(`home.return.faqs.${item.key}.html_2`) }}
                  </a>
                  {{ $t(`home.return.faqs.${item.key}.html_3`) }}
                </div>
              </template>
              <template v-else-if="item.type === 'steps'">
                <ol class="faq-steps">
                  <li
                    v-for="(step, stepIndex) in item.steps"
                    :key="stepIndex"
                  >
                    {{ step }}
                  </li>
                </ol>
              </template>
              <template v-else-if="item.type === 'whatsapp'">
                {{ $t(`home.return.faqs.${item.key}.html_1`) }}
                <a
                  class="faq-link"
                  :href="$t(`home.return.faqs.${item.key}.html_2`)"
                  target="_blank"
                >
                  {{ $t(`home.return.faqs.${item.key}.html_3`) }}
                </a>
              </template>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
  <PriceTableModal
    v-if="showPriceTableModal"
    :point-id="lastTransaction?.point_id || lastTransaction?.rent_point?.id"
    :token="lastTransaction?.token"
    @hide-price-table-modal="showPriceTableModal = false"
  />
</template>

<script>
  import PriceTableModal from '@/components/PriceTableModal.vue'
  import BaseButton from '@/components/ui/BaseButton.vue'

  export default {
    name: 'RentSuccess',
    components: {
      PriceTableModal,
      BaseButton
    },
    props: {
      lastTransaction: {
        type: Object,
        default: null
      },
      isUserOnDemand: {
        type: Boolean,
        default: true
      }
    },
    emits: ['show-map'],
    data() {
      return {
        elapsedTime: 0,
        interval: null,
        showPriceTableModal: false,
        activeIndex: null
      }
    },
    computed: {
      faqItems() {
        const faqs = this.$tm('home.return.faqs')
        if (!faqs || typeof faqs !== 'object') return []

        return Object.keys(faqs)
          .filter(key => key !== 'title')
          .map(key => {
            const item = faqs[key]
            let type = 'text'
            const keys = Object.keys(item)
            const steps = keys
              .filter(k => k.startsWith('step_'))
              .sort((a, b) => {
                const numA = parseInt(a.split('_')[1])
                const numB = parseInt(b.split('_')[1])
                return numA - numB
              })
              .map(k => this.$t(`home.return.faqs.${key}.${k}`))

            if (steps.length > 0) {
              type = 'steps'
            } else if (key === 'were_to_return') {
              type = 'html'
            } else if (key === 'return_failed') {
              type = 'whatsapp'
            }

            return {
              key,
              title: this.$t(`home.return.faqs.${key}.title`),
              type,
              steps
            }
          })
      }
    },
    mounted() {
      if (this.lastTransaction) {
        this.elapsedTime = this.lastTransaction.elapsed_time || 0
        this.startTimer()
      }
    },
    beforeUnmount() {
      this.stopTimer()
    },
    methods: {
      startTimer() {
        this.stopTimer()
        this.interval = setInterval(() => {
          this.elapsedTime += 1
        }, 1000)
      },
      stopTimer() {
        if (this.interval) {
          clearInterval(this.interval)
          this.interval = null
        }
      },
      handleShowPriceTable() {
        this.showPriceTableModal = true
      },
      handleShowMap() {
        this.$emit('show-map')
      },
      toggleAccordion(index) {
        this.activeIndex = this.activeIndex === index ? null : index
      },
      startTransition(el) {
        el.style.height = el.scrollHeight + 'px'
      },
      endTransition(el) {
        el.style.height = ''
      }
    }
  }
</script>

<style scoped lang="scss">
  @use '@/main';

  .rent-success-wrapper {
    width: 100%;
    max-width: 700px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .status-card {
    background: white;
    border-radius: 16px;
    padding: 16px;
    border-left: 4px solid main.$color-energy-orange;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    margin-top: 24px;
  }

  .status-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .status-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .status-dot-pulse {
    width: 8px;
    height: 8px;
    background-color: main.$color-energy-orange;
    border-radius: 50%;
    position: relative;
    animation: pulse 2s infinite;

    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      background-color: inherit;
      border-radius: 50%;
      animation: pulse-ring 2s infinite;
    }
  }

  .status-title {
    font-size: 0.85rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: main.$color-energy-orange;
  }

  .info-row {
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding: 8px 0;
  }

  .info-item {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    justify-content: center;
  }

  .info-icon {
    font-size: 1.25rem;
    color: main.$color-energy-dark-gray;
    opacity: 0.7;
  }

  .info-content {
    display: flex;
    flex-direction: column;
  }

  .info-label {
    font-size: 0.75rem;
    color: #6b7280;
    margin-bottom: 2px;
  }

  .info-value {
    font-size: 1.1rem;
    font-weight: 700;
    color: #111827;
  }

  .info-divider {
    width: 1px;
    height: 30px;
    background-color: #e5e7eb;
  }

  .free-time-bar {
    background-color: #fff7ed;
    border-radius: 8px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.85rem;
  }

  .free-icon {
    color: #f97316;
  }

  .free-text {
    color: #9a3412;
  }

  .success-card {
    flex-direction: column !important; // Override select-card default
    padding: 24px !important;
    text-align: center !important;
  }

  .icon-circle {
    width: 64px;
    height: 64px;
    background-color: #f0fdf4;
    color: #22c55e;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    margin: 0 auto;
  }

  .success-title {
    font-weight: 800;
    color: #111827;
  }

  .success-instructions {
    font-size: 0.85rem;
    line-height: 1.4;
    max-width: 280px;
    margin: 4px auto 0;
    opacity: 0.9;
  }

  .gif-wrapper {
    background-color: #f9fafb;
    border-radius: 12px;
    padding: 16px;
    display: flex;
    justify-content: center;
  }

  .instruction-gif {
    max-width: 100%;
    height: auto;
    mix-blend-mode: multiply;
  }

  .footer-actions {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .action-btn {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background-color: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: main.$color-energy-orange;
      background-color: #fffaf7;
      transform: translateY(-1px);
      box-shadow: 0 4px 6px -1px rgb(0 0 0 / 10%);
    }

    .btn-icon {
      font-size: 1.25rem;
      color: main.$color-energy-orange;
    }

    .btn-text {
      font-size: var(--fs-sm);
      color: #4b5563;
      text-align: left;
      line-height: 1.4;
    }
  }

  .link-underline {
    text-decoration: underline;
    font-weight: 600;
    color: main.$color-energy-orange;
  }

  @keyframes pulse {
    0% {
      transform: scale(0.95);
      opacity: 0.8;
    }
    50% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(0.95);
      opacity: 0.8;
    }
  }

  @keyframes pulse-ring {
    0% {
      transform: scale(0.33);
      opacity: 0.5;
    }
    80%,
    100% {
      transform: scale(2.5);
      opacity: 0;
    }
  }

  /* FAQ Styles */
  .faq-section {
    width: 100%;
    max-width: 600px;
    margin: 32px auto 48px;
  }

  .faq-title-main {
    font-weight: 800;
    color: #111827;
    font-size: 1.25rem;
    letter-spacing: -0.02em;
  }

  .faq-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .faq-item {
    background: white;
    border-radius: 16px;
    border: 1px solid #e5e7eb;
    border-left: 4px solid #e5e7eb;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;

    &.active {
      border-left-color: main.$color-energy-orange;
      border-color: #e5e7eb;
      background-color: white;
      box-shadow:
        0 10px 15px -3px rgba(0, 0, 0, 0.05),
        0 4px 6px -2px rgba(0, 0, 0, 0.02) !important;
      transform: scale(1.01);
    }

    &:hover:not(.active) {
      border-color: main.$color-energy-orange;
      background-color: #f9fafb;
      transform: translateY(-2px);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    }
  }

  .faq-header-btn {
    width: 100%;
    padding: 20px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    outline: none !important;

    .faq-question {
      font-size: 1rem;
      font-weight: 700;
      color: #1f2937;
      line-height: 1.4;
      transition: color 0.2s;
    }

    .faq-icon {
      font-size: 0.9rem;
      color: #9ca3af;
      transition: all 0.3s ease;
    }

    &.active {
      .faq-question {
        color: main.$color-energy-orange;
      }
      .faq-icon {
        color: main.$color-energy-orange;
        transform: rotate(0deg); // Already handled by icon change in template
      }
    }
  }

  .faq-collapse {
    overflow: hidden;
  }

  .faq-content {
    padding: 0 24px 24px;
    font-size: 0.95rem;
    color: #4b5563;
    line-height: 1.6;

    .faq-steps {
      padding-left: 20px;
      margin-bottom: 0;
      li {
        margin-bottom: 10px;
        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    .faq-link {
      color: main.$color-energy-orange;
      text-decoration: underline;
      font-weight: 700;
      cursor: pointer;
      &:hover {
        opacity: 0.8;
      }
    }
  }

  /* Accordion Animation */
  .accordion-enter-active,
  .accordion-leave-active {
    transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .accordion-enter-from,
  .accordion-leave-to {
    height: 0 !important;
  }
</style>
